import React from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Clock,
  MinusCircle,
  Wrench,
  MapPin,
  Thermometer,
  User,
  Database,
} from 'lucide-react';

const STATUS_STYLES = {
  completed: {
    label: 'Completed',
    dot: 'bg-emerald-500 border-emerald-200',
    card: 'border-slate-200',
    pill: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: CheckCircle2,
    iconClass: 'text-emerald-600',
  },
  in_progress: {
    label: 'In progress',
    dot: 'bg-blue-500 border-blue-200',
    card: 'border-blue-200',
    pill: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: Loader2,
    iconClass: 'text-blue-600 animate-spin',
  },
  pending: {
    label: 'Pending',
    dot: 'bg-slate-300 border-slate-200',
    card: 'border-slate-200',
    pill: 'bg-slate-100 text-slate-600 border-slate-200',
    icon: Clock,
    iconClass: 'text-slate-500',
  },
  missing: {
    label: 'Missing — manual correction required',
    dot: 'bg-amber-500 border-amber-200',
    card: 'border-amber-300 bg-amber-50/40',
    pill: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: AlertTriangle,
    iconClass: 'text-amber-600',
  },
  manually_closed: {
    label: 'Closed via manual correction',
    dot: 'bg-emerald-500 border-emerald-200',
    card: 'border-emerald-300 bg-emerald-50/40',
    pill: 'bg-emerald-50 text-emerald-800 border-emerald-300',
    icon: Wrench,
    iconClass: 'text-emerald-700',
  },
  skipped: {
    label: 'Skipped',
    dot: 'bg-slate-400 border-slate-200',
    card: 'border-slate-200',
    pill: 'bg-slate-100 text-slate-600 border-slate-200',
    icon: MinusCircle,
    iconClass: 'text-slate-500',
  },
};

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-NO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ProductionStagesTimeline = ({ stages = [] }) => {
  return (
    <div className="relative" data-testid="optimar-stages-timeline">
      <div className="absolute left-[19px] top-2 bottom-2 w-px bg-slate-200" />
      <ol className="space-y-4">
        {stages.map((stage) => {
          const style = STATUS_STYLES[stage.status] || STATUS_STYLES.pending;
          const Icon = style.icon;
          return (
            <li
              key={stage.step_number}
              data-testid={`optimar-stage-${stage.stage_key}`}
              className="relative pl-12"
            >
              <span
                className={`absolute left-2 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full border-4 ${style.dot}`}
                aria-hidden="true"
              />
              <div className={`rounded-xl border bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] ${style.card}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-400">
                        Step {String(stage.step_number).padStart(2, '0')}
                      </span>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${style.pill}`}>
                        <Icon className={`h-3.5 w-3.5 ${style.iconClass}`} />
                        {style.label}
                      </span>
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-slate-900">
                      {stage.stage_label}
                    </h3>
                    {stage.notes && (
                      <p className="mt-1 text-sm text-slate-600">{stage.notes}</p>
                    )}
                    <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-500 sm:grid-cols-2">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{formatTimestamp(stage.timestamp)}</span>
                      </div>
                      {stage.location && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" />
                          <span className="truncate">{stage.location}</span>
                        </div>
                      )}
                      {typeof stage.temperature_celsius === 'number' && (
                        <div className="flex items-center gap-1.5">
                          <Thermometer className="h-3.5 w-3.5" />
                          <span>{stage.temperature_celsius} °C</span>
                        </div>
                      )}
                      {stage.operator && (
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5" />
                          <span className="truncate">{stage.operator}</span>
                        </div>
                      )}
                      {stage.scada_source && (
                        <div className="flex items-center gap-1.5 sm:col-span-2">
                          <Database className="h-3.5 w-3.5" />
                          <span className="truncate font-mono">{stage.scada_source}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ProductionStagesTimeline;
